import type { LandingPageSeoConfig } from './LandingPageSeoInterfaces';
import { WORKY_WEB_APP_PATHS } from '../../WorkyWebAppPaths';
import {
  buildFaqPageJsonLd,
  buildHomeCareServiceJsonLd,
  buildOrganizationJsonLd,
  buildSoftwareApplicationJsonLd,
  buildWebSiteJsonLd,
} from './LandingPageSeoJsonLd';

const WORKY_SITE_ORIGIN: string = 'https://worky.pro';
const DEFAULT_OG_IMAGE_PATH: string = '/worky-logo-for-media-sharing.jpeg';

const HOME_SOFTWARE_KEYWORDS: string =
  'healthcare staffing software, home care staffing software, healthcare agency software, staffing agency software, home care agency software, PSW staffing software, caregiver scheduling software, healthcare payroll software';

const HOMECARE_LOCAL_KEYWORDS: string =
  'private home care near me, PSW home care Mississauga, senior home care Toronto, respite care for elderly parent, home care after hospital discharge, nursing care at home GTA, private home care GTA, in-home care Toronto Mississauga';

const OPERATIONS_SEO_KEYWORDS: string =
  'healthcare staffing software, home care staffing software, healthcare agency software, staffing agency software, healthcare scheduling software, healthcare time clock software, staffing payroll software, healthcare invoicing software, PSW staffing software, caregiver scheduling software, United States and Canada healthcare payroll';

const OPERATIONS_SEO_FAQS: readonly { readonly question: string; readonly answer: string }[] = [
  {
    question: 'What is healthcare staffing software?',
    answer:
      'Healthcare staffing software helps agencies schedule qualified workers, verify time and attendance, run payroll, and generate client invoices in one workflow. Worky Operations Suite connects Smart Match, Time Clock, Worky Pay, Invoicing, Workforce, and Trust Vault for healthcare and home care agencies.',
  },
  {
    question: 'What does Worky Operations Suite include?',
    answer:
      'Worky Operations Suite includes automated client invoicing, flexible payroll for contractors and employees (Worky Pay), geolocation time clock, AI-driven Smart Match placement, a centralized workforce hub, and Trust Vault credential compliance.',
  },
  {
    question: 'Does Worky support payroll for healthcare staffing agencies?',
    answer:
      'Yes. Worky Pay supports payroll workflows from calculation through remittance so healthcare and home care staffing agencies can pay contractors and employees with fewer spreadsheet handoffs.',
  },
  {
    question: 'How does Worky Time Clock help staffing agencies?',
    answer:
      'Worky Time Clock captures clock-in and clock-out events with location context so agencies can verify worked hours, reduce timesheet disputes, and feed approved time into payroll and client billing.',
  },
  {
    question: 'Can home care staffing agencies use Worky?',
    answer:
      'Yes. Home care staffing software needs on Worky include caregiver scheduling, credential tracking, time verification, payroll, and client invoicing for PSW, nursing, and private home care agencies.',
  },
  {
    question: 'How do I book a Worky healthcare staffing software demo?',
    answer:
      'Book a 20–30 minute Worky demo to see scheduling, time clock, payroll, and invoicing connected in one healthcare staffing operations workflow. You can also contact Worky sales for an agency walkthrough.',
  },
];

const HOMECARE_FAQS: readonly {
  readonly question: string;
  readonly answer: string;
}[] = [
  {
      question: 'Who will actually be coming into my loved one’s home?',
      answer:
          'Before care begins, you’ll know which home-care provider is responsible for the service and who is expected to visit your loved one. Depending on the care plan, that may be a personal support worker, caregiver, nurse, or another qualified care professional. We help match around care needs, routine, availability, and personal preferences so the arrangement feels comfortable from the start.',
  },
  {
      question: 'How do I know the caregiver is safe and qualified?',
      answer:
          'Safety comes first. We work with established home-care providers and explain the screening, credentials, supervision, and background-check requirements that apply to the caregiver and role being proposed. Requirements can vary by province, territory, profession, and provider, so we make sure you understand what standards apply before care begins.',
  },
  {
      question: 'Who is responsible for the care?',
      answer:
          'You will always know the organization responsible for delivering and supervising the care. That provider manages the caregiver, service delivery, invoicing, and care-related concerns. Worky remains involved as your coordinator so you have someone to help if questions or issues come up.',
  },
  {
      question: 'Are the caregivers employed by Worky?',
      answer:
          'Worky coordinates care through a network of independent home-care providers across Canada. The caregiver is employed or contracted by the provider responsible for your care, not by Worky. Before you move forward, you will know which organization is providing the care, who is responsible for the caregiver, and who to contact if something needs attention.',
  },
  {
      question: 'Why use Worky instead of calling home-care agencies myself?',
      answer:
          'Instead of contacting provider after provider on your own, Worky gives you one place to explain what your family needs. We work across a broad network of experienced home-care providers and help narrow the options based on location, care needs, availability, and fit. You still know exactly which provider will deliver the care before you agree to anything.',
  },
  {
      question: 'Does Worky only provide home care in Ontario?',
      answer:
          'No. Worky works with home-care providers across Canada, not just Ontario. Availability depends on your province or territory, community, type of care required, schedule, and caregiver availability. We currently serve participating communities across Canada, excluding Quebec at this time. Share your postal code and we’ll confirm the options available near you.',
  },
  {
      question: 'How do I know if care is available in my area?',
      answer:
          'Availability varies by location, type of care, schedule, and caregiver capacity. Send us your postal code and a general idea of the support you need, and we’ll check the Worky provider network for available options in your area. You do not need to know exactly what type of care you need before contacting us.',
  },
  {
      question: 'What happens if my loved one doesn’t feel comfortable with the caregiver?',
      answer:
          'Care is personal, and the right fit matters. If the personality, communication style, routine, language, or overall match does not feel right, tell us. We’ll work with the responsible provider to understand what is not working and explore another caregiver or adjustment where available.',
  },
  {
      question: 'Can we keep the same caregiver?',
      answer:
          'Continuity is important, especially when someone is welcoming care into their home. Providers will try to keep care as consistent as possible based on scheduling and availability. If having the same caregiver is especially important to your family, tell us at the beginning so it can be considered when arranging care.',
  },
  {
      question: 'What happens if the caregiver is sick or can’t make a visit?',
      answer:
          'The responsible care provider should have a process for cancellations, schedule changes, and backup coverage. We’ll help you understand that process before care starts. If something changes unexpectedly, you should know who to contact and what the next step is rather than being left to figure it out on your own.',
  },
  {
      question: 'What if something goes wrong after care starts?',
      answer:
          'You should never feel unsure about who to call. The responsible care provider handles service delivery and caregiver concerns, and Worky can also help coordinate the next step. If there is a problem with reliability, communication, caregiver fit, scheduling, or the care arrangement itself, reach out and we’ll help get the right people involved.',
  },
  {
      question: 'How much will home care cost?',
      answer:
          'Pricing depends on your location, the type of care required, the schedule, and the provider delivering the service. Before you agree to care, you should understand the rate, minimum visit requirements, applicable fees, cancellation terms, and who will invoice you. Your first conversation with Worky is free and there is no obligation to proceed.',
  },
  {
      question: 'Will I know the price before agreeing to anything?',
      answer:
          'Yes. Our goal is to make the arrangement clear before care begins. You should know which provider is being proposed, what services are included, the expected schedule, pricing, applicable terms, and anticipated start date before deciding whether to move forward.',
  },
  {
      question: 'Do I have to pay for Worky home care out of pocket?',
      answer:
          'Worky helps families arrange private-pay home care, so families are generally responsible for the cost of the services they choose. Pricing varies by location, type of care, schedule, and provider. Before care begins, you’ll be able to review the proposed provider, rate, minimums, applicable fees, and payment terms.',
  },
  {
      question: 'Is home care covered by OHIP?',
      answer:
          'OHIP itself does not generally pay for private home-care services. In Ontario, some home and community care may be publicly funded based on eligibility and assessed need. Worky helps families arrange private home care, which can also be used alongside publicly funded services when additional support is needed.',
  },
  {
      question: 'What about publicly funded home care outside Ontario?',
      answer:
          'Public home-care programs are administered differently across Canada, and eligibility, available services, and hours can vary by province or territory. Worky arranges private home care and can help you understand how additional private support may fit alongside the publicly funded services your loved one already receives.',
  },
  {
      question: 'Can I use Worky if my loved one already receives publicly funded home care?',
      answer:
          'Yes. Families often use private home care to supplement publicly funded services when they need additional hours, more scheduling flexibility, companionship, overnight support, respite, personal care, or nursing. Public programs differ across Canada, so the private care arrangement can be built around the support your loved one already receives.',
  },
  {
      question: 'Can private insurance cover home care?',
      answer:
          'Sometimes. Coverage depends on the insurance policy, benefits plan, type of care, and provider requirements. Some extended health, long-term care, workplace, or other insurance plans may reimburse eligible home-care or nursing services. We recommend confirming coverage directly with your insurer before care begins, and the responsible provider can explain what receipts or documentation they are able to provide.',
  },
  {
      question: 'Is there a contract or long-term commitment?',
      answer:
          'That depends on the provider and care arrangement being proposed. Before care begins, you should understand any minimum hours, notice requirements, cancellation terms, or other commitments. We want those details to be clear before your family makes a decision.',
  },
  {
      question: 'How quickly can care start?',
      answer:
          'It depends on your location, the type of support required, caregiver availability, and the schedule you need. Some situations can be arranged quickly while others require more coordination. Tell us your postal code, timing, and broad care needs and we’ll help determine the earliest realistic option.',
  },
  {
      question: 'What if we need care urgently after a hospital discharge?',
      answer:
          'Tell us as early as possible. We can look for providers that offer post-hospital support and determine what availability exists in your area. We’ll also help clarify what kind of support may be needed at home so your family has a clearer plan for the transition.',
  },
  {
      question: 'What if I’m not sure what type of care my parent needs?',
      answer:
          'That is completely normal and one of the main reasons families contact us. You do not need to know whether you need companionship, personal support, nursing, respite care, or something else before calling. Tell us what has been getting harder and we’ll help you understand the available options.',
  },
  {
      question: 'Can care change if my loved one’s needs change?',
      answer:
          'Yes. Care needs can change over time. If your loved one begins needing more help, less help, different hours, or a different type of support, let us know. We can work with the responsible provider to review the existing arrangement and discuss what may need to change.',
  },
  {
      question: 'Can family members stay involved in the care plan?',
      answer:
          'Yes. With your loved one’s permission and the appropriate consent, family members can be involved in conversations about routines, preferences, schedules, and changes in care. Good home care should help families feel informed and involved while respecting the privacy and choices of the person receiving care.',
  },
  {
      question: 'What information do I need to share to get started?',
      answer:
          'Very little at first. We usually need your contact information, postal code, who needs care, the general type of help you are looking for, and when you would like support to begin. You do not need to provide detailed medical information just to have an initial conversation.',
  },
  {
      question: 'Is the first conversation really no-obligation?',
      answer:
          'Yes. The first conversation is simply an opportunity to tell us what is happening, ask questions, and understand what options may be available. You are not committing to a caregiver, provider, schedule, or care plan by requesting a callback.',
  },
];

const homeSeo: LandingPageSeoConfig = {
  title: 'Healthcare Staffing Software: Shift to Pay to Invoice | Worky',
  description:
    'Schedule qualified healthcare workers, verify time, run payroll (calculation through remittance), and generate client invoices in one agency workflow. Book a 20-minute Worky demo.',
  canonicalPath: WORKY_WEB_APP_PATHS.home,
  keywords: HOME_SOFTWARE_KEYWORDS,
  ogType: 'website',
  jsonLd: [
    buildOrganizationJsonLd(),
    buildWebSiteJsonLd(),
    buildSoftwareApplicationJsonLd(
      'Worky Healthcare Staffing Software',
      'All-in-one healthcare staffing software and home care staffing software for scheduling, time tracking, payroll, and client invoicing.',
      WORKY_WEB_APP_PATHS.home
    ),
    buildFaqPageJsonLd([
      {
        question: 'What is the best healthcare staffing software for agencies?',
        answer:
          'Worky is all-in-one healthcare staffing software built for agencies that need leads, scheduling, geolocation time clock, payroll, and automated client invoicing in one platform.',
      },
      {
        question: 'What is home care staffing software?',
        answer:
          'Home care staffing software helps agencies recruit, schedule, track, pay, and invoice caregivers and nurses for private home care, PSW visits, senior care, respite, and post-discharge support.',
      },
      {
        question: 'Does Worky support home care agencies in the GTA?',
        answer:
          'Yes. Home care and healthcare staffing agencies across Toronto, Mississauga, and the GTA use Worky to run scheduling, credentials, payroll, and client billing while coordinating private home care services.',
      },
    ]),
  ],
};

const landingPageSeoByPath: Record<string, LandingPageSeoConfig> = {
  [WORKY_WEB_APP_PATHS.home]: homeSeo,
  [WORKY_WEB_APP_PATHS.landingPageCompare]: {
    title: 'Compare Healthcare Staffing Software | Worky vs Competitors (2026)',
    description:
      'Compare Worky with WhenIWork, Bullhorn, ShiftCare, CareSmartz360, AxisCare, and other tools for healthcare staffing. See dispatch, United States and Canada payroll, billing, and invoicing side by side.',
    canonicalPath: WORKY_WEB_APP_PATHS.landingPageCompare,
    keywords: `${HOME_SOFTWARE_KEYWORDS}, healthcare staffing software comparison, Worky vs competitors`,
  },
  [WORKY_WEB_APP_PATHS.landingPageProducts]: {
    title: 'Healthcare Staffing Software Products | Sales, Operations & Marketplace | Worky',
    description:
      'Explore Worky healthcare staffing software and home care staffing software—Sales Suite, Operations Suite, and Marketplace built for modern agencies.',
    canonicalPath: WORKY_WEB_APP_PATHS.landingPageProducts,
    keywords: HOME_SOFTWARE_KEYWORDS,
    jsonLd: [
      buildSoftwareApplicationJsonLd(
        'Worky Products',
        'Sales, operations, and marketplace products for healthcare staffing software and home care staffing software buyers.',
        WORKY_WEB_APP_PATHS.landingPageProducts
      ),
    ],
  },
  [WORKY_WEB_APP_PATHS.landingPageSales]: {
    title: 'Healthcare Staffing CRM & Sales Intelligence | Worky',
    description:
      'Organize facility accounts, contacts, tasks, notes, and pipeline activity in one healthcare staffing CRM. See Worky Sales and review data coverage.',
    canonicalPath: WORKY_WEB_APP_PATHS.landingPageSales,
    keywords: 'healthcare staffing CRM, staffing agency leads software, home care agency sales software',
  },
  [WORKY_WEB_APP_PATHS.landingPageOperations]: {
    title: 'Healthcare Staffing Operations Software | Worky',
    description:
      'Connect scheduling, clock-event time verification, United States and Canada payroll (calculation through remittance), and client invoicing in one healthcare staffing operations workflow.',
    canonicalPath: WORKY_WEB_APP_PATHS.landingPageOperations,
    keywords: `${HOME_SOFTWARE_KEYWORDS}, healthcare time clock, staffing payroll software`,
    jsonLd: [
      buildSoftwareApplicationJsonLd(
        'Worky Operations Suite',
        'Operations suite for healthcare staffing software and home care staffing software: time clock, payroll, scheduling, and invoicing.',
        WORKY_WEB_APP_PATHS.landingPageOperations
      ),
    ],
  },
  [WORKY_WEB_APP_PATHS.landingPageOperationsSeo]: {
    title:
      'Healthcare Staffing Software | Scheduling, Time Clock, Payroll & Invoicing | Worky',
    description:
      'All-in-one healthcare staffing software and home care staffing software: Smart Match scheduling, geolocation time clock, United States and Canada payroll, credential Trust Vault, workforce hub, and automated client invoicing. Book a Worky demo.',
    canonicalPath: WORKY_WEB_APP_PATHS.landingPageOperationsSeo,
    keywords: OPERATIONS_SEO_KEYWORDS,
    ogType: 'website',
    jsonLd: [
      buildOrganizationJsonLd(),
      buildSoftwareApplicationJsonLd(
        'Worky Healthcare Staffing Software — Operations Suite',
        'Healthcare staffing software connecting scheduling, time clock, payroll, credentials, workforce management, and client invoicing for agencies.',
        WORKY_WEB_APP_PATHS.landingPageOperationsSeo
      ),
      buildFaqPageJsonLd(OPERATIONS_SEO_FAQS),
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `https://worky.pro${WORKY_WEB_APP_PATHS.landingPageOperationsSeo}#webpage`,
        url: `https://worky.pro${WORKY_WEB_APP_PATHS.landingPageOperationsSeo}`,
        name: 'Healthcare Staffing Software | Worky Operations',
        description:
          'Google-facing operations landing page for Worky healthcare staffing software: scheduling, time clock, payroll, credentials, and invoicing.',
        isPartOf: { '@id': 'https://worky.pro/#website' },
        about: { '@id': 'https://worky.pro/#organization' },
        primaryImageOfPage: 'https://worky.pro/worky-logo-for-media-sharing.jpeg',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://worky.pro/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Healthcare Staffing Software',
            item: `https://worky.pro${WORKY_WEB_APP_PATHS.landingPageOperationsSeo}`,
          },
        ],
      },
    ],
  },
  [WORKY_WEB_APP_PATHS.landingPageMarketplace]: {
    title: 'Healthcare Staffing Marketplace & Agency Finder | Worky',
    description:
      'Access the Worky Marketplace and agency finder to connect with specialized healthcare staffing and home care staffing partners.',
    canonicalPath: WORKY_WEB_APP_PATHS.landingPageMarketplace,
    keywords: 'healthcare staffing marketplace, home care staffing network, staffing agency finder',
  },
  [WORKY_WEB_APP_PATHS.landingPageHomecare]: {
    title:
      'Private Home Care Near Me | PSW Mississauga, Senior Care Toronto & Nursing Care GTA | Worky',
    description:
      'Find private home care near you across Toronto, Mississauga, and the GTA. Coordinate PSW home care, senior home care, respite care for an elderly parent, home care after hospital discharge, and nursing care at home—with a named Ontario provider before booking.',
    canonicalPath: WORKY_WEB_APP_PATHS.landingPageHomecare,
    keywords: HOMECARE_LOCAL_KEYWORDS,
    ogType: 'website',
    jsonLd: buildHomeCareServiceJsonLd(HOMECARE_FAQS),
  },
  [WORKY_WEB_APP_PATHS.landingPageResources]: {
    title: 'Healthcare Staffing Software Resources & Guides | Worky',
    description:
      'Guides and resources for healthcare staffing software buyers, home care staffing agencies, recruiting teams, and operations leaders.',
    canonicalPath: WORKY_WEB_APP_PATHS.landingPageResources,
    keywords: HOME_SOFTWARE_KEYWORDS,
  },
  [WORKY_WEB_APP_PATHS.landingPageCareers]: {
    title: 'Careers at Worky | Healthcare Staffing Software Company',
    description:
      'Open roles at Worky Software Solutions—help agencies scale with modern healthcare staffing software and home care staffing technology.',
    canonicalPath: WORKY_WEB_APP_PATHS.landingPageCareers,
  },
  [WORKY_WEB_APP_PATHS.landingPageBlog]: {
    title: 'Healthcare Staffing Software & Agency Operations Guides | Worky',
    description:
      'Practical guides to staffing software, scheduling, time, payroll workflows, client billing, recruiting, security, and agency operations—reviewed and updated.',
    canonicalPath: WORKY_WEB_APP_PATHS.landingPageBlog,
    keywords: `${HOME_SOFTWARE_KEYWORDS}, healthcare staffing blog, home care agency guides`,
  },
  [WORKY_WEB_APP_PATHS.privacy]: {
    title: 'Privacy Policy | Worky',
    description:
      'Worky privacy policy — how we collect, use, store, and protect personal information for Agencies, Workers, and website visitors.',
    canonicalPath: WORKY_WEB_APP_PATHS.privacy,
  },
  [WORKY_WEB_APP_PATHS.eula]: {
    title: 'End User License Agreement | Worky',
    description:
      'Worky End User License Agreement — terms governing download and use of the Worky mobile application from the App Store and Google Play.',
    canonicalPath: WORKY_WEB_APP_PATHS.eula,
  },
  [WORKY_WEB_APP_PATHS.deleteMyData]: {
    title: 'Delete My Data | Worky',
    description: 'Request deletion of your personal data from Worky.',
    canonicalPath: WORKY_WEB_APP_PATHS.deleteMyData,
  },
  [WORKY_WEB_APP_PATHS.contactSales]: {
    title: 'Contact Sales | Worky Healthcare Staffing Software',
    description:
      'Contact Worky sales to see healthcare staffing and homecare software for scheduling, timeclock, worker pay, and client invoicing. New agencies can qualify for up to $1,000 of incentives.',
    canonicalPath: WORKY_WEB_APP_PATHS.contactSales,
    keywords: `${HOME_SOFTWARE_KEYWORDS}, contact Worky sales, homecare agency software demo`,
  },
  [WORKY_WEB_APP_PATHS.leadSuccess]: {
    title: 'Thank You | Worky',
    description:
      'Thanks for contacting Worky. Our sales team will follow up shortly. You can also book a demo.',
    canonicalPath: WORKY_WEB_APP_PATHS.leadSuccess,
  },
  [WORKY_WEB_APP_PATHS.login]: {
    title: 'Worky Login | Healthcare Staffing Software Sign In',
    description:
      'Worky login for Worky Pro — sign in to healthcare staffing software and home care staffing software for time clock, payroll, and client invoicing.',
    canonicalPath: WORKY_WEB_APP_PATHS.login,
  },
  [WORKY_WEB_APP_PATHS.signup]: {
    title: 'Worky Sign Up | Start Healthcare Staffing Software Free',
    description:
      'Sign up for Worky Pro — create your account for healthcare staffing software and home care staffing software covering scheduling, time clock, payroll, and invoicing.',
    canonicalPath: WORKY_WEB_APP_PATHS.signup,
  },
};

export {
  WORKY_SITE_ORIGIN,
  DEFAULT_OG_IMAGE_PATH,
  HOMECARE_FAQS,
  OPERATIONS_SEO_FAQS,
  homeSeo,
  landingPageSeoByPath,
};
